export type UtmTouch = {
  source?: string;
  medium?: string;
  campaign?: string;
  content?: string;
  term?: string;
};

const FIRST_KEY = 'seenoty_utm_first_touch';
const LAST_KEY = 'seenoty_utm_last_touch';

function trim(v: string | null | undefined): string | undefined {
  if (v == null) return undefined;
  const t = v.trim();
  return t.length > 0 ? t : undefined;
}

/** Align with backend: Meta ads may send utm_source=meta, fb, or ig */
function normalizeSource(source?: string): string | undefined {
  if (!source) return undefined;
  const s = source.trim().toLowerCase();
  switch (s) {
    case 'meta':
    case 'fb':
    case 'facebook':
      return 'facebook';
    case 'ig':
    case 'instagram':
      return 'instagram';
    case 'tt':
    case 'tiktok':
      return 'tiktok';
    case 'google':
    case 'googleads':
    case 'gads':
      return 'google';
    default:
      return s;
  }
}

export function touchHasAnyValues(touch?: UtmTouch | null): boolean {
  if (!touch) return false;
  return Boolean(
    touch.source || touch.medium || touch.campaign || touch.content || touch.term
  );
}

/** Merge: keep existing fields; fill only empty slots from incoming. */
export function mergeTouch(base: UtmTouch | null | undefined, incoming: UtmTouch): UtmTouch {
  const source = normalizeSource(incoming.source ?? base?.source);
  return {
    source,
    medium: incoming.medium ?? base?.medium,
    campaign: incoming.campaign ?? base?.campaign,
    content: incoming.content ?? base?.content,
    term: incoming.term ?? base?.term,
  };
}

export function parseUtmFromSearchParams(params: URLSearchParams): UtmTouch | null {
  const touch: UtmTouch = {
    source: normalizeSource(trim(params.get('utm_source'))),
    medium: trim(params.get('utm_medium')),
    campaign: trim(params.get('utm_campaign')),
    content: trim(params.get('utm_content')),
    term: trim(params.get('utm_term')),
  };
  if (!touchHasAnyValues(touch)) return null;
  return touch;
}

/** TikTok/Meta/Google click IDs when ad links omit utm_source / utm_content / utm_term */
function parseClickIds(params: URLSearchParams): UtmTouch {
  const touch: UtmTouch = {};
  const ttclid = trim(params.get('ttclid'));
  const fbclid = trim(params.get('fbclid'));
  const gclid = trim(params.get('gclid'));
  if (ttclid) {
    touch.content = ttclid;
    touch.source = 'tiktok';
    touch.medium = 'paid';
  }
  if (fbclid) {
    touch.term = fbclid;
    if (!touch.source) {
      touch.source = 'facebook';
      touch.medium = 'paid';
    }
  } else if (gclid) {
    touch.term = gclid;
    if (!touch.source) {
      touch.source = 'google';
      touch.medium = 'cpc';
    }
  }
  return touch;
}

export function inferFromReferrer(referrer: string): UtmTouch | null {
  const r = referrer.toLowerCase();
  if (!r) return null;
  if (r.includes('tiktok.com')) return { source: 'tiktok', medium: 'paid' };
  if (r.includes('facebook.com') || r.includes('fb.com') || r.includes('fb.me') || r.includes('l.facebook.com'))
    return { source: 'facebook', medium: 'paid' };
  if (r.includes('instagram.com')) return { source: 'instagram', medium: 'paid' };
  if (r.includes('google.') || r.includes('googleadservices.com'))
    return { source: 'google', medium: 'cpc' };
  return null;
}

function readStored(key: string): UtmTouch | null {
  try {
    const raw = localStorage.getItem(key);
    if (!raw) return null;
    return JSON.parse(raw) as UtmTouch;
  } catch {
    return null;
  }
}

function writeStored(key: string, touch: UtmTouch): void {
  try {
    localStorage.setItem(key, JSON.stringify(touch));
  } catch {
    /* quota / private mode */
  }
}

/** Everything we can read from the current page (UTMs, click IDs, referrer). */
export function captureFromCurrentPage(): UtmTouch | null {
  if (typeof window === 'undefined') return null;
  const params = new URLSearchParams(window.location.search);
  let touch: UtmTouch = {};

  const utm = parseUtmFromSearchParams(params);
  if (utm) touch = mergeTouch(touch, utm);

  const clicks = parseClickIds(params);
  if (touchHasAnyValues(clicks)) touch = mergeTouch(touch, clicks);

  const ref = inferFromReferrer(typeof document !== 'undefined' ? document.referrer || '' : '');
  if (ref) touch = mergeTouch(touch, ref);

  return touchHasAnyValues(touch) ? touch : null;
}

export function captureUtmFromCurrentUrl(): void {
  const incoming = captureFromCurrentPage();
  if (!incoming) return;

  const prevLast = readStored(LAST_KEY);
  writeStored(LAST_KEY, mergeTouch(prevLast, incoming));

  const prevFirst = readStored(FIRST_KEY);
  if (!prevFirst) writeStored(FIRST_KEY, incoming);
  else writeStored(FIRST_KEY, mergeTouch(prevFirst, incoming));
}

export function getFirstTouch(): UtmTouch | null {
  return readStored(FIRST_KEY);
}

export function getLastTouch(): UtmTouch | null {
  return readStored(LAST_KEY);
}

export function getAttributionForOrder(): { firstTouch?: UtmTouch; lastTouch?: UtmTouch } {
  const pageTouch = captureFromCurrentPage();
  let lastTouch = readStored(LAST_KEY);
  let firstTouch = readStored(FIRST_KEY);

  if (pageTouch) {
    lastTouch = mergeTouch(lastTouch, pageTouch);
    firstTouch = mergeTouch(firstTouch, pageTouch);
    writeStored(LAST_KEY, lastTouch);
    writeStored(FIRST_KEY, firstTouch);
  } else if (typeof document !== 'undefined') {
    const refOnly = inferFromReferrer(document.referrer || '');
    if (refOnly) {
      lastTouch = mergeTouch(lastTouch, refOnly);
      firstTouch = mergeTouch(firstTouch, refOnly);
    }
  }

  return {
    ...(touchHasAnyValues(firstTouch) ? { firstTouch: firstTouch! } : {}),
    ...(touchHasAnyValues(lastTouch) ? { lastTouch: lastTouch! } : {}),
  };
}

/** Best source string for session / traffic bucket on first visit */
export function getBestUtmSourceForSession(): string | undefined {
  const last = getLastTouch();
  if (last?.source) return last.source;
  if (typeof window === 'undefined') return undefined;
  const params = new URLSearchParams(window.location.search);
  return trim(params.get('utm_source')) ?? inferFromReferrer(document.referrer || '')?.source;
}
